import { observer } from 'mobx-react-lite'
import { useEffect, useRef, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import {
  Box,
  Text,
  SimpleGrid,
  Card,
  Group,
  Badge,
  Menu,
  ActionIcon,
  Skeleton,
  Button,
  Drawer,
  TextInput,
  Stack,
  Loader,
  Tabs,
  Switch
} from '@mantine/core'
import { IoBookmarkOutline, IoBookmark, IoEllipsisVertical, IoTrash, IoAdd, IoSearch, IoOpenOutline, IoImageOutline, IoHeartOutline } from 'react-icons/io5'
import { useStore } from '../../app/stores/store'
import agent from '../../app/api/agent'
import AuthGateCta from '../../app/common/ui/AuthGateCta'
import type { UserFirearmDto } from '../../app/models/gunVault'
import type { FirearmModelSearchItem } from '../../app/models/search'
import type { WishlistItemDto } from '../../app/api/agent'
import { format } from 'date-fns'
import { toast } from 'react-toastify'

// --- Vault card ---

interface VaultCardProps {
  item: UserFirearmDto;
  onRemove: (id: string) => void;
  onTogglePin: (id: string) => void;
}

function VaultCard ({ item, onRemove, onTogglePin }: VaultCardProps) {
  const history = useHistory()
  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      sx={{
        border: item.isPinned ? '2px solid var(--gs-gold)' : undefined,
        transition: 'box-shadow 200ms ease',
        '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,.15)' },
        position: 'relative'
      }}
    >
      {item.coverImageUrl && (
        <Card.Section mb="sm">
          <img
            src={item.coverImageUrl}
            alt={`${item.make} ${item.model}`}
            style={{ width: '100%', height: 120, objectFit: 'cover' }}
          />
        </Card.Section>
      )}

      <Group position="apart" align="flex-start" noWrap>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Text
            weight={700}
            size="sm"
            lineClamp={1}
            component={Link}
            to={`/registry/${item.firearmModelId}`}
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'var(--gs-accent)' } }}
          >
            {item.make} {item.model}
          </Text>
          {item.platform && (
            <Badge size="xs" variant="light" radius="sm" mt={4}>
              {item.platform}
            </Badge>
          )}
          {item.caliber && (
            <Text size="xs" color="dimmed" mt={2}>
              {item.caliber}
            </Text>
          )}
        </Box>

        <Group spacing={4} noWrap>
          {item.isPinned && (
            <IoBookmark size={14} color="var(--gs-gold)" title="Pinned" />
          )}
          <Menu withinPortal position="bottom-end" shadow="md">
            <Menu.Target>
              <ActionIcon size="sm" variant="subtle">
                <IoEllipsisVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                icon={<IoOpenOutline size={14} />}
                onClick={() => history.push(`/registry/${item.firearmModelId}`)}
              >
                View in Registry
              </Menu.Item>
              <Menu.Item
                icon={item.isPinned ? <IoBookmark size={14} /> : <IoBookmarkOutline size={14} />}
                onClick={() => onTogglePin(item.id)}
              >
                {item.isPinned ? 'Unpin' : 'Pin'}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                icon={<IoTrash size={14} />}
                onClick={() => onRemove(item.id)}
              >
                Remove from Vault
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>

      {item.notes && (
        <Text size="xs" color="dimmed" mt="xs" lineClamp={2}>
          {item.notes}
        </Text>
      )}

      {item.isVerified && (
        <Text size="xs" mt="xs" sx={{ color: '#3FB950' }}>
          ✓ Verified
        </Text>
      )}
    </Card>
  )
}

// --- Add Firearm Drawer ---

interface AddFirearmDrawerProps {
  opened: boolean;
  onClose: () => void;
  inVaultIds: Set<string>;
  onAdd: (firearmModelId: string) => Promise<void>;
}

function AddFirearmDrawer ({ opened, onClose, inVaultIds, onAdd }: AddFirearmDrawerProps) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<FirearmModelSearchItem[]>([])
  const [searching, setSearching] = useState(false)
  const [adding, setAdding] = useState<string | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (!opened) {
      setQuery('')
      setResults([])
    }
  }, [opened])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    const q = query.trim()
    if (q.length < 2) {
      setResults([])
      return
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const data = await agent.Search.search(q)
        setResults(data.firearmModels ?? [])
      } catch {
        setResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [query])

  const handleAdd = async (id: string) => {
    setAdding(id)
    try {
      await onAdd(id)
    } finally {
      setAdding(null)
    }
  }

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 16, textTransform: 'uppercase' }}>
          Add to Vault
        </Text>
      }
      position="right"
      size={420}
      padding="md"
    >
      <TextInput
        placeholder="Search the Registry…"
        icon={<IoSearch size={14} />}
        value={query}
        onChange={(e) => setQuery(e.currentTarget.value)}
        autoFocus
        mb="md"
      />

      {searching && (
        <Box sx={{ textAlign: 'center', padding: '24px 0' }}>
          <Loader size="sm" />
        </Box>
      )}

      {!searching && query.trim().length >= 2 && results.length === 0 && (
        <Text size="sm" color="dimmed" sx={{ textAlign: 'center', padding: '24px 0' }}>
          No firearm models found for "{query}".
        </Text>
      )}

      {!searching && query.trim().length < 2 && (
        <Text size="sm" color="dimmed" sx={{ textAlign: 'center', padding: '24px 0' }}>
          Type at least 2 characters to search.
        </Text>
      )}

      {!searching && results.length > 0 && (
        <Stack spacing="xs">
          {results.map((m) => {
            const alreadyIn = inVaultIds.has(m.id)
            return (
              <Box
                key={m.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  background: 'var(--gs-surface)',
                  border: '1px solid var(--gs-border)',
                  borderRadius: 8
                }}
              >
                {m.coverImageUrl
                  ? (
                  <img
                    src={m.coverImageUrl}
                    alt=""
                    style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }}
                  />
                    )
                  : (
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 6,
                      background: 'var(--gs-surface2)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--gs-muted)',
                      flexShrink: 0
                    }}
                  >
                    <IoImageOutline size={18} />
                  </Box>
                    )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Text size="sm" weight={600} lineClamp={1}>
                    {m.make} {m.model}
                  </Text>
                  {(m.platform || m.caliber) && (
                    <Text size="xs" color="dimmed">
                      {[m.platform, m.caliber].filter(Boolean).join(' · ')}
                    </Text>
                  )}
                </Box>
                <Button
                  size="xs"
                  variant={alreadyIn ? 'light' : 'filled'}
                  color={alreadyIn ? 'green' : 'orange'}
                  disabled={alreadyIn}
                  loading={adding === m.id}
                  onClick={() => handleAdd(m.id)}
                  sx={{ flexShrink: 0 }}
                >
                  {alreadyIn ? 'In Vault' : 'Add'}
                </Button>
              </Box>
            )
          })}
        </Stack>
      )}
    </Drawer>
  )
}

// --- Wishlist card ---

function WishlistCard ({
  item,
  onRemove,
  onTogglePublic
}: {
  item: WishlistItemDto;
  onRemove: (id: string) => void;
  onTogglePublic: (id: string, isPublic: boolean) => Promise<void>;
}) {
  const history = useHistory()
  const [toggling, setToggling] = useState(false)
  return (
    <Card
      withBorder
      padding="md"
      radius="md"
      sx={{
        transition: 'box-shadow 200ms ease',
        '&:hover': { boxShadow: '0 8px 28px rgba(0,0,0,.15)' },
        border: '1px solid var(--gs-border)',
        background: 'var(--gs-surface)'
      }}
    >
      {item.firearmModelCoverImageUrl && (
        <Card.Section mb="sm">
          <img
            src={item.firearmModelCoverImageUrl}
            alt={`${item.firearmModelMake} ${item.firearmModelModel}`}
            style={{ width: '100%', height: 120, objectFit: 'cover' }}
          />
        </Card.Section>
      )}
      <Group position="apart" align="flex-start" noWrap>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Text
            weight={700}
            size="sm"
            lineClamp={1}
            component={Link}
            to={`/registry/${item.firearmModelId}`}
            sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { color: 'var(--gs-accent)' } }}
          >
            {item.firearmModelMake} {item.firearmModelModel}
          </Text>
          {item.firearmModelPlatform && (
            <Badge size="xs" variant="light" radius="sm" mt={4}>
              {item.firearmModelPlatform}
            </Badge>
          )}
          <Text size="xs" color="dimmed" mt={4}>
            Added {format(new Date(item.addedAt), 'MMM d, yyyy')}
          </Text>
        </Box>
        <Group spacing={4} noWrap>
          <Menu withinPortal position="bottom-end" shadow="md">
            <Menu.Target>
              <ActionIcon size="sm" variant="subtle">
                <IoEllipsisVertical size={14} />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                icon={<IoOpenOutline size={14} />}
                onClick={() => history.push(`/registry/${item.firearmModelId}`)}
              >
                View in Registry
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                color="red"
                icon={<IoTrash size={14} />}
                onClick={() => onRemove(item.id)}
              >
                Remove from Wishlist
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
      {item.notes && (
        <Text size="xs" color="dimmed" mt="xs" lineClamp={2}>
          {item.notes}
        </Text>
      )}
      <Switch
        mt="sm"
        size="xs"
        label="Show on public wishlist"
        description="When your wishlist is public, only checked items appear for others."
        checked={item.isPublic}
        disabled={toggling}
        onChange={async (e) => {
          setToggling(true)
          try {
            await onTogglePublic(item.id, e.currentTarget.checked)
          } catch {
            toast.error('Could not update wishlist item.')
          } finally {
            setToggling(false)
          }
        }}
        styles={{ label: { fontSize: 12 }, description: { fontSize: 11 } }}
      />
    </Card>
  )
}

// --- Main page ---

export default observer(function GunVaultPage () {
  const { gunVaultStore, wishlistStore, userStore } = useStore()
  const { items, loading, loadVault, removeFirearm, togglePin, pinnedItems, unpinnedItems } = gunVaultStore
  const [loaded, setLoaded] = useState(false)
  const [wishlistLoaded, setWishlistLoaded] = useState(false)
  const [addDrawerOpen, setAddDrawerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<string | null>('vault')
  const [wishlistPublic, setWishlistPublic] = useState(true)
  const [wishlistPrivacyLoading, setWishlistPrivacyLoading] = useState(false)

  const inVaultIds = new Set(items.map((i) => i.firearmModelId))

  useEffect(() => {
    if (!loaded) {
      loadVault().finally(() => setLoaded(true))
    }
  }, [loadVault, loaded])

  useEffect(() => {
    if (userStore.isLoggedIn && !wishlistLoaded) {
      wishlistStore.loadWishlist().finally(() => setWishlistLoaded(true))
    }
  }, [userStore.isLoggedIn, wishlistStore, wishlistLoaded])

  useEffect(() => {
    if (!userStore.user?.username) return
    agent.Profiles.get(userStore.user.username).then((p) => setWishlistPublic(p.isWishlistPublic !== false))
  }, [userStore.user?.username])

  if (!userStore.isLoggedIn) {
    return (
      <Box sx={{ maxWidth: 600, margin: '0 auto' }}>
        <AuthGateCta
          action="build your Gun Vault"
          pitch="Log every firearm you own — pin your favourites, track your collection, and share your vault with the community."
          bullets={[
            'Pinned firearms front-and-centre on your profile',
            'Link each firearm to its Registry entry with full specs',
            'Your vault is visible to followers — show off your collection'
          ]}
        />
      </Box>
    )
  }

  const header = (
    <Group position="apart" mb="xl">
      <Box>
        <Text sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700, fontSize: 22, color: 'var(--gs-text)', marginBottom: 4 }}>GUN VAULT</Text>
        <Text size="sm" sx={{ fontFamily: 'DM Sans, sans-serif', color: 'var(--gs-muted)' }}>
          {loaded ? `${items.length} firearm${items.length === 1 ? '' : 's'} in your vault` : 'Your personal firearm collection.'}
        </Text>
      </Box>
      <Button
        leftIcon={<IoAdd size={16} />}
        onClick={() => setAddDrawerOpen(true)}
        sx={{
          background: 'var(--gs-surface)',
          border: '1.5px solid var(--gs-border)',
          color: 'var(--gs-text)',
          fontFamily: 'DM Sans, sans-serif',
          '&:hover': { borderColor: 'var(--gs-border-bright)' }
        }}
      >
        Add Firearm
      </Button>
    </Group>
  )

  if (loading && !loaded) {
    return (
      <Box>
        {header}
        <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="lg">
          {[1, 2, 3].map((n) => (
            <Skeleton key={n} height={160} radius="md" />
          ))}
        </SimpleGrid>
      </Box>
    )
  }

  return (
    <Box>
      {header}

      <Tabs
        value={activeTab}
        onTabChange={setActiveTab}
        sx={{ '& .mantine-Tabs-tabsList': { borderBottom: '1px solid var(--gs-border)', marginBottom: 20 } }}
      >
        <Tabs.List>
          <Tabs.Tab value="vault" sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
            My Vault <Text component="span" sx={{ fontFamily: "'DM Mono', monospace", color: 'var(--gs-accent)', fontSize: 11, marginLeft: 4 }}>{items.length}</Text>
          </Tabs.Tab>
          <Tabs.Tab value="wishlist" icon={<IoHeartOutline size={13} />} sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 600 }}>
            Wishlist <Text component="span" sx={{ fontFamily: "'DM Mono', monospace", color: 'var(--gs-accent)', fontSize: 11, marginLeft: 4 }}>{wishlistStore.items.length}</Text>
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="vault">
          {!loaded
            ? (
            <Group position="center" py="xl">
              <Loader size="md" />
            </Group>
              )
            : items.length === 0
              ? (
            <Box
              py="xl"
              px="lg"
              sx={{
                textAlign: 'center',
                backgroundColor: 'var(--gs-surface)',
                borderRadius: 8,
                border: '1px solid var(--gs-border)'
              }}
            >
              <Text size="lg" color="dimmed" mb="md">
                Your vault is empty. Browse the Registry to add firearms.
              </Text>
              <Text
                component={Link}
                to="/registry"
                size="sm"
                sx={{
                  color: 'var(--gs-accent)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Browse the Registry →
              </Text>
            </Box>
                )
              : (
            <>
              {pinnedItems.length > 0 && (
                <Box mb="xl">
                  <Group spacing="xs" mb="sm">
                    <IoBookmark size={14} color="var(--gs-gold)" />
                    <Text size="xs" color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }}>
                      Pinned
                    </Text>
                  </Group>
                  <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="lg">
                    {pinnedItems.map((item) => (
                      <VaultCard key={item.id} item={item} onRemove={removeFirearm} onTogglePin={togglePin} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
              {unpinnedItems.length > 0 && (
                <Box>
                  {pinnedItems.length > 0 && (
                    <Text size="xs" color="dimmed" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 }} mb="sm">
                      All Firearms
                    </Text>
                  )}
                  <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="lg">
                    {unpinnedItems.map((item) => (
                      <VaultCard key={item.id} item={item} onRemove={removeFirearm} onTogglePin={togglePin} />
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </>
                )}
        </Tabs.Panel>

        <Tabs.Panel value="wishlist">
          <Box
            mb="lg"
            p="md"
            sx={{
              background: 'var(--gs-surface)',
              border: '1px solid var(--gs-border)',
              borderRadius: 8
            }}
          >
            <Switch
              label="Public wishlist"
              description="Turn off to hide your entire wishlist from other members. You can still manage it here."
              checked={wishlistPublic}
              disabled={wishlistPrivacyLoading}
              onChange={async (e) => {
                const checked = e.currentTarget.checked
                if (!userStore.user?.username) return
                setWishlistPrivacyLoading(true)
                try {
                  const p = await agent.Profiles.get(userStore.user.username)
                  await agent.Profiles.update(userStore.user.username, {
                    displayName: p.displayName,
                    bio: p.bio ?? '',
                    coverImageUrl: p.coverImageUrl ?? null,
                    isVaultPublic: p.isVaultPublic !== false,
                    isWishlistPublic: checked
                  })
                  setWishlistPublic(checked)
                } catch {
                  toast.error('Could not update wishlist privacy.')
                } finally {
                  setWishlistPrivacyLoading(false)
                }
              }}
            />
          </Box>
          {wishlistStore.items.length === 0
            ? (
            <Box
              py="xl"
              px="lg"
              sx={{
                textAlign: 'center',
                backgroundColor: 'var(--gs-surface)',
                borderRadius: 8,
                border: '1px solid var(--gs-border)'
              }}
            >
              <Text size="md" color="dimmed" mb="md">
                Your wishlist is empty. Add models you want to own from the Registry.
              </Text>
              <Text
                component={Link}
                to="/registry"
                size="sm"
                sx={{
                  color: 'var(--gs-accent)',
                  textDecoration: 'none',
                  fontWeight: 600,
                  '&:hover': { textDecoration: 'underline' }
                }}
              >
                Browse the Registry →
              </Text>
            </Box>
              )
            : (
            <SimpleGrid cols={1} breakpoints={[{ minWidth: 640, cols: 2 }, { minWidth: 960, cols: 3 }]} spacing="lg">
              {wishlistStore.items.map((item) => (
                <WishlistCard
                  key={item.id}
                  item={item}
                  onRemove={(id) => wishlistStore.removeFromWishlist(id)}
                  onTogglePublic={(id, isPublic) => wishlistStore.updateItem(id, { isPublic })}
                />
              ))}
            </SimpleGrid>
              )}
        </Tabs.Panel>
      </Tabs>

      <AddFirearmDrawer
        opened={addDrawerOpen}
        onClose={() => setAddDrawerOpen(false)}
        inVaultIds={inVaultIds}
        onAdd={async (id) => {
          await gunVaultStore.addFirearm(id)
        }}
      />
    </Box>
  )
})
